import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {MsgBoxService} from '../../shared/msg-box.service';
import {downloadBlob, errorCode, isAdvancedUser, isDefined, isRepresentative} from '../../commons/commons';
import {ErrorCode} from '../../api/errors';
import {I18NService} from '../../i18n/i18n.service';
import {LoginService} from '../../shared/login.service';
import {AuthService, UserProfile} from 'auth-commons';
import {combineLatest, forkJoin, Observable, of, Subject} from 'rxjs';
import {
    EC_DOCUMENT_PP,
    ECJuridicalType,
    ECLandRegistryData,
    ECMemberRelation,
    ECRepresentative,
    ECRepresentativePersonalData,
    EnergyCommunityNamedPod,
    POD_ABSORBED_ENERGY_DEVICE,
    POD_AVERAGE_ABSORBED_POWER_DEVICE,
    POD_AVERAGE_INJECTED_POWER_DEVICE,
    POD_INJECTED_ENERGY_DEVICE
} from 'smag-commons/cfg/hierarchy';
import {SmagApiService} from '../../api/smag-api.service';
import {
    ECAcceptedDocument,
    ECDocument,
    ECLegalForm,
    ECMemberDocument,
    ECMemberPOD,
    ECMemberRelationType
} from 'smag-commons/cfg/hierarchy/ec';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {map, switchMap, takeUntil} from 'rxjs/operators';
import {ConfigurationService} from '../../shared/configuration.service';
import {HierarchyService} from '../../shared/hierarchy.service';
import {hasFieldEntityDevice} from '../../shared/common';
import {SubjectInfo} from 'smag-commons/dto/api/subject-info';
import {CustomerConfig} from 'smag-commons/dto/api';
import {environment} from '../../../environments/environment';
import {
    getPodEnergyProfile,
    getPodProfileInfo,
    mkLandRegistryDataString,
    PodEnergyProfiles,
    PodProfileInfo
} from '../../shared/energy-community';
import {
    getHolderMemberRelations,
    getMemberTargetPodRelations,
    getOwnerMemberRelations,
    isMemberPodRelationActive,
    prettySize,
    sortMemberRelations
} from '../../shared/ec-members';
import {googleCompatibleTextAddress} from 'smag-commons/fcn';

interface ECAcceptedDocumentWithId extends ECAcceptedDocument {
    id: string;
}

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();

    isAdv: Observable<boolean>;
    isRep: Observable<boolean>;
    subjectInfo: SubjectInfo;
    subjectInfoSubject: Subject<SubjectInfo> = new Subject<SubjectInfo>();
    representativeData: ECRepresentative;
    profile: UserProfile;
    privacyPolicyDocumentId: string;
    privacyPolicyLink: string;
    cookiePolicyLink = environment.cookiesLink;
    termsAndConditionsLink: string;
    specificClausesLink: string;
    ecDocuments: ECDocument[] = [];
    showError: boolean;
    eCRepresentativePersonalData: ECRepresentativePersonalData;

    readonly isDefined = isDefined;
    readonly addressToString = googleCompatibleTextAddress;
    readonly prettySize = prettySize;
    readonly ECJuridicalType = ECJuridicalType;
    readonly ECLegalForm = ECLegalForm;
    readonly ECMemberRelationType = ECMemberRelationType;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly i18n: I18NService,
                private readonly router: Router,
                private readonly loginService: LoginService,
                private readonly msgBoxService: MsgBoxService,
                private readonly authService: AuthService,
                private readonly apiService: SmagApiService,
                private readonly configurationService: ConfigurationService,
                private readonly hierarchyService: HierarchyService) {
    }

    ngOnInit() {
        this.profile = this.authService.profile;
        this.isAdv = isAdvancedUser(of(this.profile));
        this.isRep = isRepresentative(of(this.profile));
        combineLatest([this.isAdv, this.isRep])
            .subscribe(([isAdv, isRep]) => {
                    if (isAdv) {
                        this.initAdv();
                    } else if (isRep) {
                        this.initRepresentative();
                    } else {
                        this.initSuper();
                    }
                }
            );
    }

    onProfile(profile: UserProfile) {
        this.loginService.notifyProfileChange(profile);
    }

    onInfoOccurred(event: string) {
        this.msgBoxService.info(event);
    }

    onErrorOccurred(event: [string, Error]) {
        const msg = mkErrorMessage(event[1]) || event[0];
        this.msgBoxService.error(this.i18n.translate(msg), event[1]);
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    changePasswordNavigate(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.router.navigate([this.cfg.auth.changePasswordRoute]).then();
    }

    getPodRelations(pod: string): ECMemberRelation[] {
        const podRelations = getMemberTargetPodRelations(this.subjectInfo.subject.relations, pod);
        return [
            ...sortMemberRelations(getOwnerMemberRelations(podRelations)),
            ...sortMemberRelations(getHolderMemberRelations(podRelations))
        ];
    }

    isPodActive(relation: ECMemberRelation): boolean {
        return isMemberPodRelationActive(relation);
    }

    hasFile(doc: ECMemberDocument): boolean {
        return !!doc.filename;
    }

    onClickDownloadMemberDocument(doc: ECMemberDocument) {
        this.apiService.getMemberDocument(this.subjectInfo.subject.id, doc.id).subscribe(([data, filename]) => {
            downloadBlob(data, filename);
        });
    }

    onClickDownloadCommunityDocument(doc: ECDocument) {
        this.apiService.getECDocument(this.subjectInfo.subject.network_id, doc.id).subscribe(([data, filename]) => {
            downloadBlob(data, filename);
        });
    }

    openECDocId(docId: string) {
        this.apiService.getECDocument(this.subjectInfo.subject.network_id, docId)
            .subscribe(([blob, filename]) => window.open(URL.createObjectURL(blob), '_blank'));
    }

    openECDocLink(link: string) {
        window.open(link, '_blank');
    }

    navigateToEntity(podCode: string) {
        this.apiService.findEntityByPOD(podCode)
            .pipe(takeUntil(this.destroyed))
            .subscribe((code) => {
                this.router.navigate(['/c', 'ec', 'resources'], {
                    queryParams: {
                        id: code.id
                    },
                    queryParamsHandling: 'merge'
                }).then();
            });
    }

    monitoredPod(ecPod: EnergyCommunityNamedPod): boolean {
        if (!ecPod) {
            return false;
        }
        const podProfile = getPodEnergyProfile(ecPod);
        if (podProfile === PodEnergyProfiles.CONSUMER) {
            return hasFieldEntityDevice(ecPod.pod, POD_ABSORBED_ENERGY_DEVICE) ||
                hasFieldEntityDevice(ecPod.pod, POD_AVERAGE_ABSORBED_POWER_DEVICE);
        } else if (podProfile === PodEnergyProfiles.PROSUMER) {
            return (hasFieldEntityDevice(ecPod.pod, POD_ABSORBED_ENERGY_DEVICE) ||
                    hasFieldEntityDevice(ecPod.pod, POD_AVERAGE_ABSORBED_POWER_DEVICE)) &&
                (hasFieldEntityDevice(ecPod.pod, POD_INJECTED_ENERGY_DEVICE) ||
                    hasFieldEntityDevice(ecPod.pod, POD_AVERAGE_INJECTED_POWER_DEVICE));
        } else if (podProfile === PodEnergyProfiles.PRODUCER) {
            return hasFieldEntityDevice(ecPod.pod, POD_INJECTED_ENERGY_DEVICE) ||
                hasFieldEntityDevice(ecPod.pod, POD_AVERAGE_INJECTED_POWER_DEVICE);
        }
        return false;
    }

    mkLandRegistryDataString(lrd: ECLandRegistryData): string | null {
        return mkLandRegistryDataString(lrd, v => this.i18n.translate(v));
    }

    getPodProfileInfo(pod: ECMemberPOD): PodProfileInfo {
        if (!pod.resource) {
            return null;
        }
        return getPodProfileInfo(pod.resource);
    }

    mkPodProfileClass(profile: PodEnergyProfiles): string {
        switch (profile) {
            case PodEnergyProfiles.CONSUMER:
                return 'consumer';
            case PodEnergyProfiles.PRODUCER:
                return 'producer';
            case PodEnergyProfiles.PROSUMER:
                return 'prosumer';
        }
    }

    onClickDownloadAcceptedDocument(docId: string) {
        if (docId) {
            this.apiService.getECDocument(this.subjectInfo.subject.network_id, docId).subscribe(([data, filename]) => downloadBlob(data, filename));
        }
    }

    get acceptedDocuments(): ECAcceptedDocumentWithId[] {
        return (this.subjectInfo?.subject?.accepted_documents ?? []).map(doc => ({
            ...doc,
            id: this.ecDocuments.find(ecDoc => ecDoc.document_type === doc.name)?.id
        }))
            .filter(doc => doc.name !== EC_DOCUMENT_PP);
    }

    get documentsWithoutPrivacy() {
        return this.ecDocuments.filter(doc => doc.document_type !== EC_DOCUMENT_PP);
    }

    private initRepresentative() {
        this.apiService.findRepresentativeByU3id(this.profile.u3id)
            .subscribe(representative => this.representativeData = representative);
    }

    private initAdv() {
        this.apiService.getSubjectInfo()
            .subscribe(subject => {
                this.subjectInfo = subject;
                this.subjectInfoSubject.next(subject);
            });

        this.subjectInfoSubject.pipe(
            switchMap(subject =>
                this.hierarchyService.hierarchyIds.pipe(
                    takeUntil(this.destroyed),
                    switchMap(h =>
                        forkJoin(subject.subject.pods.map(pod =>
                                this.apiService.findEntityByPOD(pod.pod).pipe(
                                    switchMap(idResponse =>
                                        this.apiService.getECPod(h[idResponse.id].parent.id, idResponse.id)
                                            .pipe(map(resource => ({...pod, resource})))
                                    )
                                )
                            )
                        ).pipe(map(resources => this.subjectInfo.subject.pods = resources))
                    )
                )
            ),
            takeUntil(this.destroyed)
        ).subscribe(() => {
        }, error => {
            this.showError = true;
            console.error(error);
        });

        this.subjectInfoSubject.pipe(
            switchMap(subjectInfo => forkJoin(
                [this.apiService.findRepresentativeByNetwork(subjectInfo.subject.network_id),
                    this.apiService.listECDocuments(1, 100, subjectInfo.subject.network_id, '', true),
                    this.apiService.getCustomerConfig(subjectInfo.subject.network_id)])),
            takeUntil(this.destroyed)
        ).subscribe(([rep, docs, customerConfig]) => {
            this.eCRepresentativePersonalData = rep?.personal_data;
            this.ecDocuments = docs.results;
            this.mkLegalDocuments(customerConfig, docs.results);
        }, error => {
            this.showError = true;
            console.error(error);
        });
    }

    private initSuper() {
        this.apiService.getCustomerConfig()
            .subscribe(customerConfig => this.mkLegalDocuments(customerConfig));
    }

    private mkLegalDocuments(customerConfig: CustomerConfig, ecDocuments?: ECMemberDocument[]) {
        const ppDocument = ecDocuments?.find(doc => doc.document_type && doc.document_type === EC_DOCUMENT_PP);
        if (ppDocument) {
            this.privacyPolicyDocumentId = ppDocument.id;
        } else {
            this.privacyPolicyLink = customerConfig.privacy_policy_link;
        }
        this.termsAndConditionsLink = customerConfig.control_panel_terms_and_conditions_link;
        this.specificClausesLink = customerConfig.control_panel_specific_clauses_link;
    }
}

function mkErrorMessage(err: Error): string {
    const code = errorCode(err);
    switch (code) {
        case ErrorCode.EMAIL_ALREADY_EXIST:
            return 'Email address already exists';
        default:
            return 'An error occurred while updating user profile';
    }
}

